import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ProjectSprintData } from '../../../../core/modules/project/services/project.sprint.data.service';
import { TrackeritemBreakdownService } from '../../services/trackeritem.breakdown.service';

/**
 * "Breakdown" tab (#65237): treats a Tracker Item with children (parent_id) like a
 * mini-sprint. Lean state (0 children, Iteration 3) shows the cross-tab CTA + quick-add.
 * Full state (Iteration 4) adds Remaining/Burndown charts (adapted from
 * project.sprint.details.open.html / reused unmodified via ProjectSprintData and
 * <project-sprint-burndown>), and a Sub-Tasks/Add-Sub-Tasks toggle. The progress bar itself
 * lives in the edit-header instead (BreakdownHeaderComponent, visible on every tab, same
 * pattern as <dependency-header>) -- TrackeritemBreakdownService is shared with that component
 * so both read/write the same rel.children fetch instead of duplicating it. Not yet wired into
 * the tab strip nav -- Iteration 5.
 */
@Component({
  selector: 'ti-breakdown',
  template: require('./trackeritem.breakdown-tab.component.html')
})
export class BreakdownTabComponent implements OnInit, OnChanges {
  @Input() project: any;
  @Input() tracker: any;
  @Input() trackeritem: any;
  @Input() acls: any;

  activeView: string = 'list';
  remainingChartOptions: any = null;
  remainingChartData: any = null;

  constructor(
    private projectSprintData: ProjectSprintData,
    private breakdownService: TrackeritemBreakdownService
  ) {}

  // This component is mounted by UI-Router's `component:` view binding (tracker.routes.config.ts,
  // the .breakdown state), not a normal Angular template [input] binding -- ngOnChanges firing
  // reliably for the *initial* input set can't be assumed the way it can for a template-bound
  // component. ngOnInit is guaranteed to run exactly once regardless of how inputs were set, so
  // it's the reliable place to kick off the initial load; ngOnChanges stays to catch later swaps
  // if this same component instance is ever reused for a different trackeritem.
  ngOnInit(): void {
    this.ensureChartsData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.trackeritem) {
      this.ensureChartsData();
    }
  }

  private ensureChartsData(): void {
    // The route's tiWithChildren resolve (tracker-resolves.service.ts), or BreakdownHeaderComponent
    // mounting first (it's always visible, this tab isn't), may already have fetched
    // rel.children/remaining/burndown -- ensureChildrenLoaded() reuses that instead of
    // re-fetching, and only hits the network if it genuinely isn't there yet.
    this.breakdownService.ensureChildrenLoaded(this.trackeritem).then(() => {
      this.refreshCharts();
    });
  }

  onChildrenCreated(): void {
    this.breakdownService.refreshChildren(this.trackeritem).then(() => {
      this.refreshCharts();
      this.activeView = 'list';
    });
  }

  private refreshCharts(): void {
    this.remainingChartOptions = this.projectSprintData.getRemainingChartOptions();
    this.remainingChartData = this.projectSprintData.getRemainingChartData(this.trackeritem);
  }
}
